import PropTypes from 'prop-types'
import { forwardRef, memo, useMemo } from 'react'
import { normalizeSizeString } from 'app/util/sizes'
import useActiveStateAnimationChildren from 'lib/react/hooks/useActiveStateAnimationChildren'
import useFallbackRef from 'lib/react/hooks/useFallbackRef'
import classNames from 'lib/util/classNames'
import styles from './Body.module.scss'

/**
 * The `Body`
 *
 * @param {object} props - the component props
 * @returns {React.ReactElement} the element
 */
const Body = forwardRef((props, forwardedRef) => {
  const { Tag, align, children, className, color, justify, size, ...rest } =
    props
  const ref = useFallbackRef(forwardedRef)
  const classNameOutput = useMemo(
    () =>
      classNames([
        className,
        styles.body,
        styles[`align-${align}`],
        styles[`justify-${justify}`],
        styles[`color-${color}`],
        styles[`size-${normalizeSizeString(size)}`]
      ]),
    [className, color, size, align, justify]
  )

  // Animation
  useActiveStateAnimationChildren(ref, true)

  return (
    <Tag ref={ref} className={classNameOutput} {...rest}>
      {children}
    </Tag>
  )
})

// Display name
Body.displayName = 'Body'

/** @type {object} */
Body.propTypes = {
  children: PropTypes.node,
  color: PropTypes.string,
  Tag: PropTypes.elementType,
  className: PropTypes.string,
  size: PropTypes.string,
  align: PropTypes.string,
  justify: PropTypes.string
}

/** @type {object} */
Body.defaultProps = {
  Tag: 'div',
  color: 'a',
  className: '',
  size: 'standard',
  align: 'left',
  justify: 'left'
}

// Memoize
export default memo(Body)
